import { every, includes, intersection, isEmpty } from "lodash";
import { groupTagsByCategory } from "../components/tagSelect/tags";
import { DisplayMode } from "../data/BaseEntity";
import type { OrderStatus } from "../data/BaseEntity";
import { getOrderListDisplayMode } from "../data/dataUtils";
import { ISO_DATE_FORMAT } from "../dayjs";
import type { GroupType, SavedOrderWithNames } from "./orderTypes";
import type { OrdersIndex } from "./ordersIndex";
import type { OrdersSearchCriteria } from "./topBar/useOrderSearch";
import { useOrders } from "./useOrders";

export type DisplayModeCounts = Record<DisplayMode.WARNING | DisplayMode.OVERDUE, number>;

interface OrderWithDisplayMode {
  readonly order: SavedOrderWithNames;
  readonly displayMode: DisplayMode;
}

export const useFilteredOrders = (search: OrdersSearchCriteria, showHistorical: boolean) => {
  const { orders, ordersIndex, isLoading } = useOrders({
    from: search.startDate.format(ISO_DATE_FORMAT),
    to: search.endDate.format(ISO_DATE_FORMAT),
    showHistorical,
  });
  const tagsByCategory = groupTagsByCategory(search.tags);
  const ordersMatchingOtherFilters: OrderWithDisplayMode[] = filterOrdersByText(orders ?? [], ordersIndex, search.text)
    .filter((o) => isCustomer(o, search.customerId))
    .filter((o) => isAgentAssigned(o, search.agentId))
    .filter((o) => isStatus(o, search.statuses))
    .filter((o) => isContactPerson(o, search.contactPersonEmail))
    .filter((o) => hasTag(o, tagsByCategory))
    .filter((o) => isGroupType(o, search.groupTypes))
    .map((order) => ({ order, displayMode: getOrderListDisplayMode(order) }));
  const ordersToShow = ordersMatchingOtherFilters
    .filter(({ displayMode }) => isDisplayMode(displayMode, search.displayModes))
    .map(({ order }) => order);

  return { orders: ordersToShow, displayModeCounts: countDisplayModes(ordersMatchingOtherFilters), isLoading };
};

function countDisplayModes(orders: OrderWithDisplayMode[]): DisplayModeCounts {
  return {
    [DisplayMode.OVERDUE]: orders.filter(({ displayMode }) => displayMode === DisplayMode.OVERDUE).length,
    [DisplayMode.WARNING]: orders.filter(({ displayMode }) => displayMode === DisplayMode.WARNING).length,
  };
}

function filterOrdersByText(orders: SavedOrderWithNames[], index: OrdersIndex, text: string) {
  if (!text) {
    return orders;
  }
  const queries = text
    .split(/ +/)
    .filter((v) => !isEmpty(v))
    .map((v) => v.toLocaleLowerCase());
  return Object.values(index)
    .filter((e) => every(queries, (query) => includes(e.index, query)))
    .map((e) => e.id)
    .map((id) => orders.find((o) => o.id === id))
    .filter((o): o is SavedOrderWithNames => o !== undefined);
}

function isAgentAssigned(order: SavedOrderWithNames, agentId: string | null): boolean {
  if (agentId === null) {
    return true;
  }
  const supervisorId = order.supervisorId ?? order.customerAccountManagerId;
  if (!supervisorId) {
    return false;
  }
  return agentId == supervisorId;
}

function isCustomer(order: SavedOrderWithNames, customerId: string | null): boolean {
  return !customerId || order.customerId === customerId;
}

function isStatus(order: SavedOrderWithNames, statuses: OrderStatus[]): boolean {
  if (isEmpty(statuses)) {
    return true;
  }
  return statuses.includes(order.status);
}

function isDisplayMode(displayMode: DisplayMode, displayModes: DisplayMode[]): boolean {
  if (isEmpty(displayModes)) {
    return true;
  }
  return displayModes.includes(displayMode);
}

function isContactPerson(order: SavedOrderWithNames, contactPersonEmail: string | null): boolean {
  return !contactPersonEmail || order.customerContactPersonEmail === contactPersonEmail;
}

function hasTag(order: SavedOrderWithNames, tagsByCategory: Record<string, string[]>): boolean {
  return Object.keys(tagsByCategory).length === 0 || tagsMatches(order, tagsByCategory);
}

function tagsMatches({ tags }: SavedOrderWithNames, tagsByCategory: Record<string, string[]>) {
  return Object.entries(tagsByCategory).every(([category, tagsGroup]) => {
    const tagsForCategory = tags.filter((t) => t.category === category).map((t) => t.value);
    return intersection(tagsGroup, tagsForCategory).length > 0;
  });
}

function isGroupType(order: SavedOrderWithNames, groupTypes: GroupType[]): boolean {
  if (isEmpty(groupTypes)) {
    return true;
  }
  return order.groupType !== undefined && groupTypes.includes(order.groupType);
}
